import { useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  LockKeyhole,
  UploadCloud,
  X,
} from "lucide-react";
import { useReportBuilderStore } from "../../store/reportBuilderStore";
import { formatFileSize } from "../../utils/format";
import { useAppNavigate } from "../../app/router";

const progressSteps = [
  "Reading workbook",
  "Detecting report sections",
  "Parsing daily revenue",
  "Parsing tenant accounts",
  "Parsing expenses",
  "Validating totals",
];

export function UploadPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useAppNavigate();
  const [dragging, setDragging] = useState(false);
  const [selected, setSelected] = useState<File | null>(null);
  const {
    importWorkbookFile,
    isParsing,
    parseProgress,
    fatalError,
    workbookMetadata,
  } = useReportBuilderStore();

  async function handleFile(file?: File) {
    if (!file) return;
    setSelected(file);
    await importWorkbookFile(file);
    if (useReportBuilderStore.getState().workbookMetadata) {
      navigate("/report/import");
    }
  }

  return (
    <div className="upload-page page-wrap">
      <section className="upload-intro">
        <div className="eyebrow">Private by design</div>
        <h1>Turn your monthly workbook into a landlord-ready report.</h1>
        <p>
          Upload the Excel file you already use. ParkLedger separates daily
          revenue, tenant collections, and expenses, checks the numbers, and
          prepares a polished PDF.
        </p>
        <div className="trust-row">
          <div>
            <LockKeyhole size={18} />
            <span>
              <strong>Local processing</strong>
              Your financial data never leaves this browser.
            </span>
          </div>
          <div>
            <FileSpreadsheet size={18} />
            <span>
              <strong>Flexible import</strong>
              Handles operational and landlord workbook layouts.
            </span>
          </div>
          <div>
            <CheckCircle2 size={18} />
            <span>
              <strong>Reconciled totals</strong>
              Every report value is recalculated from detail.
            </span>
          </div>
        </div>
      </section>

      <section className="upload-panel" aria-labelledby="upload-title">
        <div className="panel-heading">
          <span className="step-number">01</span>
          <div>
            <h2 id="upload-title">Upload monthly workbook</h2>
            <p>.xlsx or .xls · maximum 20 MB</p>
          </div>
        </div>

        <div
          className={`dropzone ${dragging ? "dragging" : ""} ${fatalError ? "has-error" : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void handleFile(event.dataTransfer.files[0]);
          }}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ")
              inputRef.current?.click();
          }}
          role="button"
          tabIndex={0}
          aria-label="Upload Excel workbook"
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(event) => void handleFile(event.target.files?.[0])}
            hidden
          />
          <span className="upload-icon">
            <UploadCloud size={28} />
          </span>
          <strong>Drop your workbook here</strong>
          <span>or click to browse your computer</span>
          <button className="button primary" type="button">
            Choose Excel file
          </button>
        </div>

        {selected ? (
          <div className="file-row">
            <span className="file-icon">
              <FileSpreadsheet size={20} />
            </span>
            <span>
              <strong>{selected.name}</strong>
              <small>{formatFileSize(selected.size)}</small>
            </span>
            {!isParsing ? (
              <button
                className="icon-button"
                onClick={() => setSelected(null)}
                aria-label="Remove file"
              >
                <X size={16} />
              </button>
            ) : null}
          </div>
        ) : null}

        {isParsing ? (
          <div className="import-progress" role="status" aria-live="polite">
            <div className="progress-head">
              <span>{parseProgress || "Reading workbook"}</span>
              <span>Working locally</span>
            </div>
            <div className="progress-track">
              <span />
            </div>
            <div className="progress-labels">
              {progressSteps.slice(0, 3).map((step) => (
                <span key={step}>{step}</span>
              ))}
            </div>
          </div>
        ) : null}

        {fatalError ? (
          <div className="alert error" role="alert">
            <strong>We couldn’t import this workbook.</strong>
            <span>{fatalError}</span>
          </div>
        ) : null}

        {workbookMetadata && !isParsing ? (
          <button
            className="button primary full"
            onClick={() => navigate("/report/import")}
          >
            Review imported data <ArrowRight size={16} />
          </button>
        ) : null}

        <div className="privacy-note">
          <LockKeyhole size={14} />
          <span>
            Your workbook is processed locally in this browser and is not
            uploaded. Workbook data is cleared when you create a new report or
            close the tab.
          </span>
        </div>
        <a
          className="sample-link"
          href="/test-fixtures/standard-input.xlsx"
          download
        >
          Download a sanitized sample workbook
        </a>
      </section>
    </div>
  );
}
